/**
 * "Assess now": one publication, one impact assessment.
 *
 * **No client is chosen, since 31 August 2026.** The document describes the publication and the
 * businesses it affects; Joanne applies it to a client herself, from what she knows of them. The
 * clients she has tagged on the row ride along as a reminder printed on the document, and choose
 * nothing.
 *
 * **Preview, then spend.** The brief's rule for this button is that the click is not blind, and
 * the preview is what makes it so: it names the publication, says what the run will cost and
 * warns when there is already a draft of it.
 * It reads the database and nothing else, so it is free and she can look every time.
 *
 * **It never waits.** Generating takes three model calls at Opus effort over a whole publication —
 * minutes — so the click accepts the work and a worker does it. This polls until the job reaches a
 * terminal state, and she can close the tab: the answer is on the job record, not in this page.
 *
 * **Refused is not failed, and the wording says so.** A run that stopped because the link turned
 * out to be a landing page rather than the document, because the publication is longer than one
 * assessment reads, or because the item has no URL at all, did exactly what it should. Calling
 * that a failure would teach her to ignore failures — which is what the Mills Review did on
 * 20 September 2026, when a refusal that spent nothing reached her as a red "Something broke"
 * naming an environment variable. The status was the bug, not this screen; see
 * `assessment/errors.py`.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../api";
import { formatDate } from "../config";
import { isAction, priorityOf } from "../nextstep";
import { PICKER_SORTS, pickerName, pickerOptions, type PickerSort } from "../pickerlist";
import type { Assessment, AssessmentPreview, Item } from "../types";
import { DocumentActions } from "./DocumentView";

/** How often to ask whether the worker is done. Minutes of work, so there is no hurry. */
const POLL_MS = 5000;

/**
 * How long a generated document survives in S3 before the lifecycle rule removes it.
 *
 * Stated on screen rather than left to be discovered. This is the one thing the system makes that
 * costs real money to make again — an Excel export is a click, an impact assessment is minutes of
 * model time — and it is not versioned, not backed up and not regenerated automatically. Mirrors
 * `var.export_retention_days` in `infra/variables.tf`; change them together.
 */
const RETENTION_DAYS = 365;

/** Pence as money. The API converts from dollars and rounds, so nothing rounds twice. */
export function money(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

/** Stop polling eventually: a job that never terminates is a bug, and a spinner is not a report. */
const POLL_LIMIT = 180;

const STATUS_LABEL: Record<Assessment["status"], string> = {
  queued: "Queued",
  generating: "Generating…",
  ready: "Ready",
  refused: "Not run",
  failed: "Failed",
};

function Refusals({ job }: { job: Assessment }) {
  if (!job.rejected.length && !job.unresolved.length && !job.problems.length) return null;

  return (
    <div className="refusals">
      {job.problems.length > 0 && (
        <p className="small ai-warning">
          <strong>{job.problems.length} internal inconsistency.</strong> The document says so on
          its last page. This should never happen — worth telling Craig.
        </p>
      )}
      {job.rejected.length > 0 && (
        <details>
          <summary className="small">
            {job.rejected.length} statement{job.rejected.length === 1 ? "" : "s"} rejected before
            drafting
          </summary>
          <ul className="small">
            {job.rejected.map((rejection, index) => (
              <li key={index}>
                <strong>{rejection.label || "(unlabelled)"}</strong> — {rejection.reason}
              </li>
            ))}
          </ul>
          <p className="small muted">
            Each of these was proposed as an obligation and refused because the publication does
            not contain the words quoted for it. They are in the document's annex too.
          </p>
        </details>
      )}
      {job.unresolved.length > 0 && (
        <details>
          <summary className="small">
            {job.unresolved.length} reference{job.unresolved.length === 1 ? "" : "s"} could not be
            verified
          </summary>
          <ul className="small">
            {job.unresolved.map((citation, index) => (
              <li key={index}>
                <code>{citation.text}</code> — {citation.note}
              </li>
            ))}
          </ul>
          <p className="small muted">
            These appear in the document as <code>[unverified: …]</code> rather than as links.
            Check them against the publication before the paper goes anywhere.
          </p>
        </details>
      )}
    </div>
  );
}

function Result({ job }: { job: Assessment }) {
  if (job.status === "refused") {
    return (
      <div className="preview">
        <p>
          <strong>Not run.</strong> {job.message}
        </p>
        {job.can_narrow && (
          <p className="small muted">
            It is listed under <strong>In progress</strong> below, where you can run it on
            sections you choose or on passages the AI picks.
          </p>
        )}
      </div>
    );
  }

  if (job.status === "failed") {
    // A run stopped part-way by its own budget or its own clock is `failed` too, and it is not
    // free: the 1 September 2026 Consumer Duty run spent £7.72 and produced nothing. Telling her
    // "nothing was written, trying again is safe" would be true about the document and a lie
    // about the money, and she would press it again. So the retry line follows the spend.
    const spent = job.cost_pence ?? 0;
    return (
      <div className="preview">
        <p className="ai-warning">
          <strong>Something broke.</strong> {job.message}
        </p>
        <p className="small muted">
          {spent > 0 ? (
            <>
              No document was produced, and this run still cost {money(spent)}. Running it again
              would cost about the same, so it is worth checking the publication first.
            </>
          ) : (
            <>Nothing was written and nothing was spent. Trying again is safe — a re-run replaces
            rather than duplicates.</>
          )}
        </p>
      </div>
    );
  }

  if (job.status !== "ready") return null;

  const counts = job.counts || {};
  return (
    <div className="preview">
      <p className="preview-count">
        <strong>{job.reference}</strong> — {counts.obligations ?? 0} obligation
        {counts.obligations === 1 ? "" : "s"}, {counts.applicable ?? 0} apply,{" "}
        {counts.actions ?? 0} action{counts.actions === 1 ? "" : "s"}
        {counts.out_of_scope ? <>, {counts.out_of_scope} documented out of scope</> : null}
      </p>

      {job.url && (
        <p className="ready">
          <a href={job.url} target="_blank" rel="noreferrer noopener">
            Download {job.filename}
          </a>{" "}
          <DocumentActions url={job.url} filename={job.filename} />{" "}
          <span className="muted small">
            — a draft, watermarked as one on every page. The link expires shortly; it is minted
            fresh each time this list loads.
          </span>
        </p>
      )}

      {job.expired && (
        <p className="small ai-warning">
          <strong>The document has been deleted.</strong> Drafts are kept for {RETENTION_DAYS}{" "}
          days and this one is older than that. What it found is still recorded above; the Word
          file is not, and generating it again costs what the first one cost.
        </p>
      )}

      <Refusals job={job} />

      {job.notes.length > 0 && (
        <ul className="small muted">
          {job.notes.map((note, index) => (
            <li key={index}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The last thing between her and the money.
 *
 * **In the card, not a browser dialog** — the same argument the delete cross makes: a browser
 * dialog cannot show the figures, cannot be styled to distinguish an estimate from a measurement,
 * and arrives on top of the thing she is reading. This sits where the button was, names the
 * publication, and says what it will cost against what is left.
 *
 * The estimate is honest about being one. A run's cost varies by an order of magnitude with the
 * length of the publication — a news item against a hundred-page Policy Statement — so the basis
 * is printed with the figure and an unmeasured default says so in as many words.
 */
export function Confirm({
  preview,
  busy,
  onCancel,
  onProceed,
  reading,
}: {
  preview: AssessmentPreview;
  busy: boolean;
  onCancel: () => void;
  onProceed: () => void;
  /**
   * For a narrower re-run, what it will read — "the 3 sections you chose (84,000 characters)".
   * Its presence also withholds the "declined last time" warning, which is about reading the
   * whole publication and is exactly what a narrower run is the answer to.
   */
  reading?: string;
}) {
  const { estimate, month } = preview;
  const overBudget = estimate.pence > month.remaining_pence;

  return (
    <div className="preview confirm-run">
      <p>
        Generating this document is predicted to cost <strong>{money(estimate.pence)}</strong>,
        which will be taken from your API budget, which has{" "}
        <strong>{money(month.remaining_pence)}</strong> remaining.
      </p>

      {/*
        The ceiling, stated separately and plainly. The prediction above is a median of past runs
        and cannot know how long this publication is — on a 400,000-character consultation it
        said about £1 and the run spent £7.72 before the guard stopped it. This is the number
        that holds whatever the document turns out to be.
      */}
      <p className="small">
        However long the publication turns out to be, the run <strong>stops at{" "}
        {money(estimate.ceiling_pence)}</strong> and reports what it spent. A very long
        publication — a consultation, or a made-rules appendix — is refused before anything is
        spent rather than read expensively.
      </p>

      <p className="small muted">
        {estimate.measured ? "Predicted from " : "Estimated: "}
        {estimate.basis}. This month you have spent {money(month.spent_pence)} of{" "}
        {money(month.budget_pence)} across {month.used} run{month.used === 1 ? "" : "s"}. Billed by
        Anthropic in US dollars, converted at one dollar to £{month.rate.toFixed(2)}.
      </p>

      {/*
        A previous refusal, repeated where the money is. The Preview lists it among the warnings,
        but the confirm replaces the Preview — so on 1 October 2026 she was quoted a price for the
        Mills Review five minutes after it had been declined for length, and pressed it.
      */}
      {reading && (
        <p className="small">
          It will read <strong>{reading}</strong>, and every quotation is checked against the
          whole publication.
        </p>
      )}

      {!reading && preview.existing?.status === "refused" && (
        <p className="small ai-warning">
          <strong>This was declined last time and nothing was spent:</strong>{" "}
          {preview.existing.message} Running it again will most likely be declined the same way.
        </p>
      )}

      {overBudget && (
        <p className="small ai-warning">
          <strong>This is more than the budget has left.</strong> Nothing stops you — but it will
          take the month over, and the run after it will be refused until the month resets.
        </p>
      )}

      <p className="small muted">
        <strong>{preview.item.title}</strong>. It runs in the background and takes a few
        minutes; you can close the tab.
      </p>

      <div className="row confirm-actions">
        <button type="button" className="primary" disabled={busy} onClick={onProceed}>
          {busy ? "Starting…" : `Generate it — ${money(estimate.pence)}`}
        </button>
        <button type="button" className="link" disabled={busy} onClick={onCancel}>
          Cancel, spend nothing
        </button>
      </div>
    </div>
  );
}

function Preview({ preview }: { preview: AssessmentPreview }) {
  return (
    <div className="preview">
      <p className="small">
        <strong>{preview.item.title}</strong>
        {preview.item.regulator && <span className="muted"> · {preview.item.regulator}</span>}
      </p>

      {/*
        Her own filing, echoed back — not a finding about these clients. The document has not
        judged them and this line must not read as though it has, which is why it says "you have
        filed this" rather than naming them as affected.
      */}
      {(preview.reminder.clients.length > 0 || preview.reminder.tags.length > 0) && (
        <p className="small muted">
          You have filed this
          {preview.reminder.clients.length > 0 && (
            <> against <strong>{preview.reminder.clients.join(", ")}</strong></>
          )}
          {preview.reminder.tags.length > 0 && (
            <> under {preview.reminder.tags.join(", ")}</>
          )}
          . That is printed on the document as a reminder; it does not change what it says.
        </p>
      )}

      {preview.blockers.map((blocker, index) => (
        <p key={index} className="small ai-warning">
          {blocker}
        </p>
      ))}
      {preview.warnings.map((warning, index) => (
        <p key={index} className="small muted">
          {warning}
        </p>
      ))}

      <p className="small muted">
        Predicted {preview.cost}. This month: {money(preview.month.spent_pence)} of{" "}
        {money(preview.month.budget_pence)} spent, {preview.month.used} of {preview.month.cap} runs
        used.
      </p>
    </div>
  );
}

export function AssessNow({
  items,
  onError,
  onFinished,
  onStarted,
}: {
  items: Item[];
  onError: (error: unknown) => void;
  /**
   * Called once when a run this card is watching reaches a terminal status. The Generated
   * documents list below loads once and does not poll, so on 1 October 2026 a finished
   * assessment was in the bucket and absent from the list she was looking at.
   */
  onFinished?: () => void;
  /** Called when a run has been accepted, so the In progress list can show it at once. */
  onStarted?: () => void;
}) {
  const [itemId, setItemId] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<PickerSort>("newest");
  const [preview, setPreview] = useState<AssessmentPreview | null>(null);
  const [job, setJob] = useState<Assessment | null>(null);
  const [busy, setBusy] = useState<"" | "preview" | "start">("");
  /** Whether she has asked to run one and is being shown what it costs first. */
  const [confirming, setConfirming] = useState(false);
  const polls = useRef(0);

  const load = useCallback(
    async (item: string) => {
      if (!item) {
        setPreview(null);
        setJob(null);
        return;
      }
      setBusy("preview");
      try {
        const answer = await api.previewAssessment(item);
        setPreview(answer);
        setJob(answer.existing);
      } catch (error) {
        onError(error);
      } finally {
        setBusy("");
      }
    },
    [onError],
  );

  useEffect(() => {
    polls.current = 0;
    // Changing the publication withdraws the confirmation. A dialog quoting a price for the one
    // she was looking at a moment ago is worse than no dialog.
    setConfirming(false);
    void load(itemId);
  }, [itemId, load]);

  // Poll only while there is something to wait for. A terminal job stops the timer, which is why
  // the effect depends on the status rather than on the job object.
  useEffect(() => {
    if (!job || job.status === "ready" || job.status === "refused" || job.status === "failed") {
      return;
    }
    // A stalled job is terminal in everything but name: the worker that would have moved it is
    // gone, so polling it again only asks the same dead record the same question.
    if (job.stalled) return;
    if (polls.current >= POLL_LIMIT) return;

    const timer = window.setTimeout(async () => {
      polls.current += 1;
      try {
        const next = await api.getAssessment(job.item_id);
        setJob(next);
        if (next.status === "ready" || next.status === "refused" || next.status === "failed") {
          onFinished?.();
        }
      } catch {
        // A poll that fails is not worth a red box: the next one will probably work, and the
        // work is happening whether or not this page is watching.
      }
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [job, onFinished]);

  const start = async () => {
    setBusy("start");
    try {
      setJob(await api.createAssessment(itemId));
      polls.current = 0;
      setConfirming(false);
      onStarted?.();
    } catch (error) {
      onError(error);
    } finally {
      setBusy("");
    }
  };

  // Stalled is not waiting. A run whose Lambda timed out stays at `generating` for ever, because
  // the process that would have written `failed` is the process that was killed — so treating it
  // as in-flight leaves her watching a spinner with no recovery and the button disabled.
  const waiting = (job?.status === "queued" || job?.status === "generating") && !job?.stalled;
  const stalled = (job?.status === "queued" || job?.status === "generating") && !!job?.stalled;
  // The HIGH-priority actions, and only those (Craig, 1 September 2026). It used to offer
  // everything she had flagged "Review impact", which is a different queue: that flag lives on
  // the review side and marks an item as worth a look, where an assessment is something she
  // decides to buy for work she has already committed to. `isAction` and `priorityOf` are the
  // same two rules the Actions screen sorts by — shared, not re-inlined here.
  const choices = items.filter((item) => isAction(item) && priorityOf(item) === "high");
  const { options, matches } = pickerOptions(choices, search, sort, itemId);
  const searching = Boolean(search.trim());
  // A list sorted by a date is only readable if that same date is on the row.
  const optionDate = (item: Item) => {
    if (sort === "deadline") return item.due_date ? `due ${formatDate(item.due_date)}` : "no deadline";
    const date = sort === "added" ? item.captured_at : item.published_at;
    return date ? formatDate(date) : "no date";
  };

  return (
    <div className="card">
      <h3>Impact assessment</h3>
      <p className="muted small">
        One publication as a Word document you can edit — the obligations it imposes, who each
        one reaches, and a proportionate action plan. It is not about one client: you decide what
        it means for yours.{" "}
        {choices.length > 0 ? (
          <>
            The list below is the {choices.length} <strong>high-priority</strong> item
            {choices.length === 1 ? "" : "s"} on your Actions screen.
          </>
        ) : (
          <>
            Nothing on the Actions screen is marked high priority, so there is nothing to assess
            yet. Set an action to <em>High</em> and it will appear here.
          </>
        )}
      </p>

      {choices.length > 0 && (
        <div className="row report-controls">
          <label className="picker-grow">
            Search
            <input
              type="search"
              value={search}
              placeholder="Title or reference"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <label>
            Sort by
            <select value={sort} onChange={(event) => setSort(event.target.value as PickerSort)}>
              {Object.entries(PICKER_SORTS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <div className="row report-controls">
        <label className="picker-grow">
          {searching ? `Publication (${matches} of ${choices.length} match)` : "Publication"}
          <select value={itemId} onChange={(event) => setItemId(event.target.value)}>
            <option value="">
              {searching && matches === 0 ? "nothing matches your search" : "choose an item…"}
            </option>
            {options.map((item) => (
              <option key={item.id} value={item.id}>
                {pickerName(item).slice(0, 90)} ({optionDate(item)})
              </option>
            ))}
          </select>
        </label>
        <button
          className="primary"
          disabled={!itemId || !!busy || waiting || confirming || preview?.can_run === false}
          // Opens the confirmation rather than starting the run. Nothing is spent until she has
          // seen the figure and pressed the second button.
          onClick={() => setConfirming(true)}
          title={
            preview?.can_run === false
              ? preview.blockers.join(" ")
              : `${preview?.cost ?? "A few model calls"}. You will be asked to confirm the cost.`
          }
        >
          {stalled
            ? "Run it again"
            : job?.status === "ready"
              ? "Generate again"
              : "Assess now"}
        </button>
      </div>

      {busy === "preview" && <p className="small muted">Reading…</p>}

      {waiting && (
        <p className="small">
          <strong>{STATUS_LABEL[job.status]}</strong> — this takes a few minutes. You can close
          the tab; the draft will be here when it is done.
        </p>
      )}

      {stalled && (
        <p className="small ai-warning">
          <strong>Stalled — nothing has happened for twenty minutes.</strong> The run almost
          certainly ran out of time on a long publication and stopped without being able to say
          so. Nothing was produced and nothing was saved; running it again is safe.
        </p>
      )}

      {preview && confirming && (
        <Confirm
          preview={preview}
          busy={busy === "start"}
          onCancel={() => setConfirming(false)}
          onProceed={start}
        />
      )}

      {preview && !confirming && !job && <Preview preview={preview} />}
      {preview && !confirming && job && !waiting && job.status !== "ready" && (
        <Preview preview={preview} />
      )}
      {job && <Result job={job} />}

      <p className="small muted">
        Nothing is sent anywhere. You download it, edit it, and decide who sees it. Each one costs
        model time to produce and you are asked to confirm the price first. The Word file is kept
        for {RETENTION_DAYS} days and then deleted — what the run found stays on this screen either
        way, but the document does not, so{" "}
        <strong>download anything you want to keep.</strong>
      </p>
    </div>
  );
}
